/**
 * Конфигурация типов тренеров
 * Определяет функциональность и UI для разных специализаций
 */

export type TrainerType = 'personal' | 'group' | 'court';

export interface TrainerTypeConfig {
  type: TrainerType;
  features: {
    showFreeSlots: boolean;         // Показывать свободные слоты
    showSchedule: boolean;           // Фиксированное расписание
    showCourtBooking: boolean;       // Бронирование кортов
    showParticipants: boolean;       // Список участников группы
    showIndividualClients: boolean;  // Индивидуальные клиенты
    allowWaitlist: boolean;          // Лист ожидания
  };
  labels: {
    bookingTitle: string;            // Заголовок секции записей
    clientsTitle: string;            // Название вкладки "Клиенты"
    clientsNavLabel: string;         // Метка навигации
    timeSlotLabel: string;           // Метка временных слотов
    addButtonLabel: string;          // Кнопка добавления
    emptyStateMessage: string;       // Сообщение "нет записей"
  };
  colors: {
    primary: string;                 // Основной цвет
    icon: string;                    // Цвет иконок
  };
}

export const TRAINER_TYPE_CONFIGS: Record<TrainerType, TrainerTypeConfig> = {
  personal: {
    type: 'personal',
    features: {
      showFreeSlots: true,
      showSchedule: false,
      showCourtBooking: false,
      showParticipants: false,
      showIndividualClients: true,
      allowWaitlist: false
    },
    labels: {
      bookingTitle: 'Ближайшие записи',
      clientsTitle: 'Мои клиенты',
      clientsNavLabel: 'Клиенты',
      timeSlotLabel: 'Свободное время',
      addButtonLabel: 'Добавить запись',
      emptyStateMessage: 'Нет предстоящих записей на сегодня'
    },
    colors: {
      primary: 'from-emerald-500 to-emerald-600',
      icon: 'text-emerald-500'
    }
  },

  group: {
    type: 'group',
    features: {
      showFreeSlots: false,
      showSchedule: true,
      showCourtBooking: false,
      showParticipants: true,
      showIndividualClients: false,
      allowWaitlist: true
    },
    labels: {
      bookingTitle: 'Ближайшие занятия',
      clientsTitle: 'Групповые занятия',
      clientsNavLabel: 'Участники',
      timeSlotLabel: 'Расписание',
      addButtonLabel: 'Записать в группу',
      emptyStateMessage: 'Нет запланированных занятий на сегодня'
    },
    colors: {
      primary: 'from-blue-500 to-blue-600',
      icon: 'text-blue-500'
    }
  },

  court: {
    type: 'court',
    features: {
      showFreeSlots: true,
      showSchedule: false,
      showCourtBooking: true,
      showParticipants: false,
      showIndividualClients: true,
      allowWaitlist: false
    },
    labels: {
      bookingTitle: 'Ближайшие бронирования',
      clientsTitle: 'Бронирования кортов',
      clientsNavLabel: 'Корты',
      timeSlotLabel: 'Свободные корты',
      addButtonLabel: 'Забронировать корт',
      emptyStateMessage: 'Нет бронирований кортов на сегодня'
    },
    colors: {
      primary: 'from-orange-500 to-orange-600',
      icon: 'text-orange-500'
    }
  }
};

/**
 * Определение типа тренера по специализации (spec_name)
 */
export function getTrainerType(specName: string): TrainerType {
  const normalized = specName.toLowerCase();

  // Групповые занятия
  if (
    normalized.includes('групп') ||
    normalized.includes('аква') ||
    normalized.includes('йога') ||
    normalized.includes('пилатес') ||
    normalized.includes('фитнес') ||
    normalized.includes('аэробика') ||
    normalized.includes('стретч')
  ) {
    return 'group';
  }

  // Корты (теннис, падел)
  if (
    normalized.includes('теннис') ||
    normalized.includes('падел') ||
    normalized.includes('корт')
  ) {
    return 'court';
  }

  // По умолчанию - персональный тренер
  return 'personal';
}

/**
 * Получить конфигурацию для тренера
 */
export function getTrainerConfig(specName: string): TrainerTypeConfig {
  const type = getTrainerType(specName);
  return TRAINER_TYPE_CONFIGS[type];
}
